/**
 * Projects API
 * Domain API via HTTP transport (apiTransport) to the backend /api RPC.
 */

import { Project, ProjectTask, ProjectPhase } from '@repo/shared-types';
import { apiTransport as router } from './transport';
import { getContext } from './core';

export const projectsApi = {
  list: async () => {
    return router.get<Project[]>('/maintenance/projects');
  },
  get: async (id: string) => {
    return router.get<Project>(`/maintenance/projects/${id}`);
  },
  create: async (project: Project) => {
    const context = getContext();
    return router.post<Project>('/maintenance/projects', project, context);
  },
  update: async (id: string, updates: Partial<Project>) => {
    const context = getContext();
    return router.patch<Project>(`/maintenance/projects/${id}`, updates, context);
  },
  delete: async (id: string) => {
    const context = getContext();
    return router.delete<{ success: boolean }>(`/maintenance/projects/${id}`, context);
  },
  tasks: {
    list: async (projectId: string) => {
      return router.get<ProjectTask[]>(`/maintenance/projects/${projectId}/tasks`);
    },
    create: async (projectId: string, taskData: Omit<ProjectTask, 'id' | 'projectId'>) => {
      const context = getContext();
      return router.post<ProjectTask>(`/maintenance/projects/${projectId}/tasks`, taskData, context);
    },
    update: async (projectId: string, id: string, updates: Partial<ProjectTask>) => {
      const context = getContext();
      return router.patch<ProjectTask>(`/maintenance/projects/${projectId}/tasks/${id}`, updates, context);
    },
  },
  phases: {
    list: async (projectId: string) => {
      return router.get<ProjectPhase[]>(`/maintenance/projects/${projectId}/phases`);
    },
    create: async (projectId: string, phaseData: Omit<ProjectPhase, 'id' | 'projectId' | 'images'>) => {
      const context = getContext();
      return router.post<ProjectPhase>(`/maintenance/projects/${projectId}/phases`, phaseData, context);
    },
    update: async (projectId: string, id: string, updates: Partial<ProjectPhase>) => {
      const context = getContext();
      return router.patch<ProjectPhase>(`/maintenance/projects/${projectId}/phases/${id}`, updates, context);
    },
    approve: async (projectId: string, id: string, notes?: string) => {
      const context = getContext();
      return router.post<ProjectPhase>(`/maintenance/projects/${projectId}/phases/${id}/approve`, { notes }, context);
    },
    reject: async (projectId: string, id: string, notes: string) => {
      const context = getContext();
      return router.post<ProjectPhase>(`/maintenance/projects/${projectId}/phases/${id}/reject`, { notes }, context);
    }
  }
};

